/**
 * API client — all data operations go through the NestJS backend.
 *
 * Token management:
 *  - Reads the JWT access token from localStorage (key: 'seedhub-auth').
 *  - Proactively refreshes the token when it is within 60 s of expiry.
 *  - On a 401 it asks the server for a new access token and retries the request
 *    once. The session is only abandoned when the server refuses the refresh
 *    token itself.
 *
 * ─── What this used to do, and what it cost ──────────────────────────────────
 *
 * The docblock promised a refresh-and-retry the code could not reach. The 401
 * branch called `getAccessToken()`, which refreshes only when the stored token
 * is within 60 s of expiry; for any other cause of a 401 it handed back the same
 * token, `freshToken !== token` was false, and the code fell through to
 * `signOut()` immediately. The retry branch was reachable only when a proactive
 * refresh happened to fire inside the same call, which is precisely the case
 * that would not have 401'd.
 *
 * So one 401, for any reason, signed the user out. It threw away a half-written
 * template, deleted the refresh cookie so that getting back in cost a password
 * AND an authenticator code, and said "your session has expired" to a session
 * with 55 minutes left. The reasons a 401 arrives without an expiry are
 * ordinary: a deploy that rotated the signing secret, clock skew, a session
 * ended on another device, or an endpoint answering 401 for a wrong six-digit
 * code.
 */
import { endSessionLocally, getAccessToken, refreshSession } from '@/services/auth'

// '/api', not an absolute origin: the Vite dev server proxies /api to the
// API (client/vite.config.ts), and the built client is served from the same
// origin as the API in production. An absolute fallback pointed at a port
// nothing listens on, so a checkout with no client/.env could not sign in at
// all, and failed as an opaque CORS error rather than as a missing variable.
const API_URL = (import.meta as any).env?.VITE_API_URL || '/api'

type RequestOptions = {
  method?: string
  body?: unknown
  params?: Record<string, string | number | boolean | null | undefined>
  headers?: Record<string, string>
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const raw = `${API_URL}${path}`
  const url = raw.startsWith('http') ? new URL(raw) : new URL(raw, window.location.origin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const { method = 'GET', body, params, headers = {} } = options

  const token = await getAccessToken()

  const fetchHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }
  if (token) {
    fetchHeaders['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(buildUrl(path, params), {
    method,
    headers: fetchHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  })

  // On a 401, actually refresh, then retry once. `isRetry` bounds it: a second
  // 401 after a successful refresh is the endpoint's own answer, not a session
  // problem, and it falls through to the error handling below so the caller sees
  // what the server said. That is how a wrong 2FA code reaches the dialog it was
  // typed into instead of ending the session.
  if (response.status === 401 && !isRetry) {
    const outcome = await refreshSession()
    if (outcome.status === 'refreshed') {
      return request<T>(path, options, true)
    }
    if (outcome.status === 'expired') {
      // The server refused the refresh token itself. This is the one case where
      // the session really is over. Cleared locally, without calling logout:
      // the cookie is already spent, and a failure handler that deletes it can
      // only ever make recovery harder.
      endSessionLocally()
      throw new Error('Your session has expired. Please sign in again.')
    }
    // 'unavailable': rate limited, offline, or the API is having a bad minute.
    // Nothing has been learnt about the session, so nothing is thrown away. The
    // original 401 is reported below and the user's work stays on screen.
  }

  if (!response.ok) {
    const text = await response.text()
    let message: string
    let errorDetails: unknown
    try {
      const json = JSON.parse(text)
      // NestJS class-validator returns `message` as a string[]; coercing it to a
      // string yields a comma-jammed blob, so join it into a readable sentence.
      message = Array.isArray(json.message)
        ? json.message.join('. ')
        : json.message || json.error || text
      errorDetails = json.details
    } catch {
      message = text
    }
    const err = new Error(message) as Error & { status?: number; details?: unknown }
    err.status = response.status
    // `details` carried through, not just the sentence.
    //
    // `GlobalExceptionFilter` forwards `details` for every status below 500,
    // and it is where the API puts the machine-readable half of a refusal: the
    // code, and the values the sentence was built from. Dropping it here left
    // the client with English prose as its only handle on an error, which is
    // how the one-organisation refusal reached a Greek customer untranslated.
    // Callers that do not look at it are unaffected.
    err.details = errorDetails
    throw err
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  // A 200 with an EMPTY body is not the same as 204, and Nest produces one
  // whenever a controller returns `null` — `GET /contacts/find-by-phone` does
  // exactly that when the lookup misses. `response.json()` throws on an empty
  // string ("Unexpected end of JSON input"), which took down quick-send for
  // every number that was not already a contact: the lookup succeeded, said
  // "no such contact", and the client treated that answer as a crash.
  //
  // Read the payload once as text and only parse when there is something there.
  const payload = await response.text()
  if (!payload) {
    return null as T
  }
  return JSON.parse(payload) as T
}

export const apiClient = {
  get: <T = unknown>(path: string, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'GET', params }),

  post: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body }),

  put: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body }),

  patch: <T = unknown>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),

  delete: <T = unknown>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
}
